// src/components/homeComponents/HeroScrollEffect.jsx
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/*
  THE TITLE CARD — same words, now a trailer moment.
  "HERE'S WHAT" assembles from scattered letters as you scroll,
  "WE DO," snaps together next, then "BETTER!" slams in from 4x scale
  with a camera shake, a shockwave ring and sparks. All scroll-scrubbed
  except the impact itself, which fires like a hit.
*/

const LINE_ONE = "HERE'S WHAT";
const LINE_TWO = "WE DO,";
const SPARK_COUNT = 14;

const HeroScrollEffect = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const stageRef = useRef(null);
  const lineOneRefs = useRef([]);
  const lineTwoRefs = useRef([]);
  const groupRef = useRef(null);
  const butRef = useRef(null);
  const betterRef = useRef(null);
  const ringRef = useRef(null);
  const sparkRefs = useRef([]);
  const flashRef = useRef(null);
  const impacted = useRef(false);

  const fireImpact = () => {
    if (impacted.current) return;
    impacted.current = true;

    // camera shake on the whole stage
    gsap.fromTo(
      stageRef.current,
      { x: 0, y: 0 },
      {
        keyframes: [
          { x: -10, y: 6, duration: 0.05 },
          { x: 9, y: -5, duration: 0.05 },
          { x: -6, y: 3, duration: 0.05 },
          { x: 4, y: -2, duration: 0.05 },
          { x: 0, y: 0, duration: 0.08 },
        ],
        ease: "none",
      }
    );

    // white flash
    gsap.fromTo(
      flashRef.current,
      { opacity: 0.35 },
      { opacity: 0, duration: 0.4, ease: "power2.out" }
    );

    // shockwave ring
    gsap.fromTo(
      ringRef.current,
      { scale: 0.2, opacity: 0.8 },
      { scale: 3.2, opacity: 0, duration: 0.9, ease: "power2.out" }
    );

    // sparks fly
    sparkRefs.current.forEach((s, i) => {
      if (!s) return;
      const angle = (i / SPARK_COUNT) * Math.PI * 2 + gsap.utils.random(-0.2, 0.2);
      const dist = gsap.utils.random(90, 220);
      gsap.fromTo(
        s,
        { x: 0, y: 0, opacity: 1, scale: gsap.utils.random(0.6, 1.2) },
        {
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist,
          opacity: 0,
          scale: 0.2,
          rotate: gsap.utils.random(-120, 120),
          duration: gsap.utils.random(0.6, 1),
          ease: "power3.out",
        }
      );
    });

    // chromatic flicker on BETTER
    gsap.fromTo(
      betterRef.current,
      { textShadow: "6px 0 0 #ff3d6e, -6px 0 0 #38e1ff" },
      {
        textShadow: "0px 0 0 #ff3d6e00, 0px 0 0 #38e1ff00",
        duration: 0.5,
        ease: "power2.out",
      }
    );
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // scatter both lines' letters before assembly
      const scatter = (el) => {
        gsap.set(el, {
          x: gsap.utils.random(-260, 260),
          y: gsap.utils.random(-160, 160),
          rotate: gsap.utils.random(-90, 90),
          opacity: 0,
        });
      };
      lineOneRefs.current.forEach(scatter);
      lineTwoRefs.current.forEach(scatter);

      gsap.set(butRef.current, { opacity: 0, y: 30 });
      gsap.set(betterRef.current, { opacity: 0, scale: 4, rotate: -4 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "+=280%",
          pin: pinRef.current,
          scrub: 1,
          anticipatePin: 1,
          onUpdate: (self) => {
            // impact fires as BETTER lands; re-arms if you scroll back
            if (self.progress > 0.82) fireImpact();
            else if (self.progress < 0.7) impacted.current = false;
          },
        },
      });

      // ACT 1 — HERE'S WHAT assembles
      tl.to(lineOneRefs.current, {
        x: 0,
        y: 0,
        rotate: 0,
        opacity: 1,
        stagger: { each: 0.05, from: "random" },
        ease: "power3.out",
        duration: 1,
      })
        // ACT 2 — WE DO, assembles bigger
        .to(
          lineTwoRefs.current,
          {
            x: 0,
            y: 0,
            rotate: 0,
            opacity: 1,
            stagger: { each: 0.06, from: "random" },
            ease: "power3.out",
            duration: 1,
          },
          "-=0.3"
        )
        // beat — hold the frame
        .to({}, { duration: 0.4 })
        // ACT 3 — the setup dims and slides up
        .to(groupRef.current, {
          y: "-16vh",
          opacity: 0.28,
          scale: 0.92,
          duration: 0.8,
          ease: "power2.inOut",
        })
        .to(
          butRef.current,
          { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" },
          "-=0.3"
        )
        // ACT 4 — BETTER! slams in
        .to(betterRef.current, {
          opacity: 1,
          scale: 1,
          rotate: 0,
          duration: 1,
          ease: "power4.in",
        })
        // settle
        .to(betterRef.current, {
          scale: 1.04,
          duration: 0.3,
          ease: "power1.inOut",
        })
        .to(betterRef.current, { scale: 1, duration: 0.3 });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef}>
      <Pin ref={pinRef}>
        <div className="flash" ref={flashRef} />
        <Stage ref={stageRef}>
          <div className="group" ref={groupRef}>
            <h1 aria-label={LINE_ONE}>
              {LINE_ONE.split("").map((ch, i) => (
                <span
                  className={`l ${ch === " " ? "sp" : ""}`}
                  key={i}
                  ref={(el) => (lineOneRefs.current[i] = el)}
                >
                  {ch === " " ? "\u00A0" : ch}
                </span>
              ))}
            </h1>
            <h2 aria-label={LINE_TWO}>
              {LINE_TWO.split("").map((ch, i) => (
                <span
                  className={`l ${["W", "E"].includes(ch) && i < 2 ? "hl" : ""} ${
                    ch === " " ? "sp" : ""
                  }`}
                  key={i}
                  ref={(el) => (lineTwoRefs.current[i] = el)}
                >
                  {ch === " " ? "\u00A0" : ch}
                </span>
              ))}
            </h2>
          </div>

          <div className="punch">
            <span className="but" ref={butRef}>
              BUT
            </span>
            <div className="better-wrap">
              <span className="ring" ref={ringRef} />
              {Array.from({ length: SPARK_COUNT }).map((_, i) => (
                <span
                  className="spark"
                  key={i}
                  ref={(el) => (sparkRefs.current[i] = el)}
                />
              ))}
              <span className="better" ref={betterRef}>
                BETTER!
              </span>
            </div>
          </div>
        </Stage>
      </Pin>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background: #08040c;
`;

const Pin = styled.div`
  height: 100vh;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .flash {
    position: absolute;
    inset: 0;
    background: #ffffff;
    opacity: 0;
    pointer-events: none;
    z-index: 9;
  }
`;

const Stage = styled.div`
  position: relative;
  text-align: center;
  will-change: transform;
  width: 100%;

  .group {
    will-change: transform, opacity;
    h1 {
      white-space: nowrap;
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: clamp(2rem, 6vw, 5.5rem);
      line-height: 1;
      color: #ffffff;
      letter-spacing: 0.02em;
    }
    h2 {
      white-space: nowrap;
      font-family: "Readex Pro", sans-serif;
      font-weight: 500;
      font-size: clamp(3rem, 10vw, 9rem);
      line-height: 1;
      color: #ffffff;
      margin-top: 0.05em;
      .hl {
        color: #9281dd;
      }
    }
    .l {
      display: inline-block;
      will-change: transform, opacity;
    }
  }

  .punch {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -38%);
    width: 100%;
    pointer-events: none;

    .but {
      display: block;
      font-family: "Montserrat";
      letter-spacing: 0.6em;
      font-size: clamp(0.8rem, 1.4vw, 1.1rem);
      color: #836caf;
      font-weight: 600;
      margin-bottom: 0.6em;
      will-change: transform, opacity;
    }

    .better-wrap {
      position: relative;
      display: inline-block;

      .better {
        display: inline-block;
        font-family: "Readex Pro", sans-serif;
        font-weight: 700;
        font-size: clamp(4rem, 16vw, 15rem);
        line-height: 0.95;
        background: linear-gradient(120deg, #9281dd 0%, #b9c5fb 55%, #ffffff 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        will-change: transform, opacity;
        filter: drop-shadow(0 20px 60px #9281dd44);
      }

      .ring {
        position: absolute;
        top: 50%;
        left: 50%;
        width: clamp(180px, 30vw, 420px);
        height: clamp(180px, 30vw, 420px);
        margin: calc(clamp(180px, 30vw, 420px) / -2) 0 0
          calc(clamp(180px, 30vw, 420px) / -2);
        border: 2px solid #b9c5fb;
        border-radius: 50%;
        opacity: 0;
        will-change: transform, opacity;
      }

      .spark {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 22px;
        height: 3px;
        border-radius: 3px;
        background: linear-gradient(90deg, #b9c5fb, #9281dd00);
        opacity: 0;
        will-change: transform, opacity;
      }
    }
  }
`;

export default HeroScrollEffect;
